import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import { useToast } from '../components/contexts/ToastContext';
import Breadcrumb from '../components/common/Breadcrumb';
import apiService from '../services/apiService';
import ROUTES from '../constants/routes';
import '../styles/pages/asignacion-permisos.css';

/**
 * Página de asignación de permisos por rol (solo ADMIN)
 *
 * Muestra una matriz de permisos agrupados por módulo.
 * Permite seleccionar qué permisos tiene cada rol y guardarlos.
 *
 * @returns {JSX.Element}
 */
function AsignacionPermisos() {
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permisos, setPermisos] = useState([]);
  const [selectedRolId, setSelectedRolId] = useState('');
  const [asignados, setAsignados] = useState(new Set()); // Set de permiso IDs asignados al rol seleccionado
  const [originalAsignados, setOriginalAsignados] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  // Cargar matriz inicial
  const fetchMatriz = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getPermisosMatriz();
      if (data.success) {
        setRoles(data.data.roles);
        setPermisos(data.data.permisos);

        // Seleccionar primer rol por defecto
        if (data.data.roles.length > 0) {
          const firstRol = data.data.roles[0];
          setSelectedRolId(firstRol.id);
          const ids = new Set(data.data.asignaciones[firstRol.id] || []);
          setAsignados(ids);
          setOriginalAsignados(ids);
        }
      }
    } catch {
      showToast('Error al cargar matriz de permisos', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchMatriz(); }, [fetchMatriz]);

  // Cambiar rol seleccionado
  const handleRolChange = useCallback(async (rolId) => {
    setSelectedRolId(rolId);
    try {
      const data = await apiService.getRolPermisos(rolId);
      if (data.success) {
        const asignadosSet = new Set(
          data.data.filter(p => p.asignado).map(p => p.id)
        );
        setAsignados(asignadosSet);
        setOriginalAsignados(asignadosSet);
      }
    } catch {
      showToast('Error al cargar permisos del rol', 'error');
    }
  }, [showToast]);

  // Toggle de un permiso
  const togglePermiso = (permisoId) => {
    setAsignados(prev => {
      const next = new Set(prev);
      if (next.has(permisoId)) {
        next.delete(permisoId);
      } else {
        next.add(permisoId);
      }
      return next;
    });
  };

  // Marcar/desmarcar todos los permisos de un módulo
  const toggleModulo = (moduloPermisos, marcar) => {
    setAsignados(prev => {
      const next = new Set(prev);
      moduloPermisos.forEach(p => {
        if (marcar) {
          next.add(p.id);
        } else {
          next.delete(p.id);
        }
      });
      return next;
    });
  };

  // Guardar cambios
  const handleSave = async () => {
    if (!selectedRolId) return;
    setSaving(true);
    try {
      const result = await apiService.updateRolPermisos(selectedRolId, Array.from(asignados));
      if (result.success) {
        showToast('Permisos actualizados exitosamente', 'success');
        setOriginalAsignados(new Set(asignados));
      } else {
        showToast(result.message || 'Error al guardar permisos', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error al guardar permisos', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Descartar cambios
  const handleDiscard = () => {
    setAsignados(new Set(originalAsignados));
  };

  const hasChanges = () => {
    if (asignados.size !== originalAsignados.size) return true;
    for (const id of asignados) {
      if (!originalAsignados.has(id)) return true;
    }
    return false;
  };

  // Agrupar permisos por módulo
  const permisosAgrupados = permisos.reduce((acc, p) => {
    if (!acc[p.modulo]) acc[p.modulo] = [];
    acc[p.modulo].push(p);
    return acc;
  }, {});

  const totalAsignados = asignados.size;
  const totalPermisos = permisos.length;

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Usuarios', to: ROUTES.GESTION_USUARIOS },
          { label: 'Roles', to: ROUTES.GESTION_ROLES },
          { label: 'Permisos' }
        ]}>
          <Link to={ROUTES.GESTION_ROLES} className="btn btn-secondary">
            <i className="fas fa-user-shield"></i> Gestionar Roles
          </Link>
        </Breadcrumb>

        {loading ? (
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin"></i> Cargando permisos...
          </div>
        ) : (
          <>
            <div className="permisos-controls">
              <div className="permisos-rol-selector">
                <label htmlFor="rol-select"><i className="fas fa-user-tag"></i> Rol:</label>
                <select
                  id="rol-select"
                  className="form-select"
                  value={selectedRolId}
                  onChange={e => handleRolChange(Number(e.target.value))}
                  style={{ minWidth: 200 }}
                >
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.nombre} ({r.codigo})</option>
                  ))}
                </select>
              </div>

              <div className="permisos-stats">
                {totalAsignados} / {totalPermisos} permisos asignados
              </div>

              {hasChanges() && hasPermission('roles.assign_permissions') && (
                <div className="permisos-bulk-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                    {' '}Guardar cambios
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleDiscard}
                    disabled={saving}
                  >
                    <i className="fas fa-undo"></i> Descartar
                  </button>
                </div>
              )}
            </div>

            <div className="permisos-matrix">
              <table>
                <thead>
                  <tr>
                    <th className="col-modulo">Permiso</th>
                    <th className="col-checkbox">Asignado</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(permisosAgrupados).map(([modulo, moduloPermisos]) => {
                    const todosAsignados = moduloPermisos.every(p => asignados.has(p.id));
                    const algunoAsignado = moduloPermisos.some(p => asignados.has(p.id));

                    return (
                      <React.Fragment key={modulo}>
                        {/* Fila de cabecera de módulo */}
                        <tr className="modulo-header">
                          <td className="col-modulo">
                            <i className="fas fa-layer-group"></i> {modulo}
                          </td>
                          <td className="col-checkbox">
                            <input
                              type="checkbox"
                              title={todosAsignados ? 'Desmarcar todos' : 'Marcar todos'}
                              checked={todosAsignados}
                              disabled={!hasPermission('roles.assign_permissions')}
                              ref={el => {
                                if (el) el.indeterminate = !todosAsignados && algunoAsignado;
                              }}
                              onChange={e => toggleModulo(moduloPermisos, e.target.checked)}
                            />
                          </td>
                        </tr>

                        {/* Filas de permisos del módulo */}
                        {moduloPermisos.map(permiso => (
                          <tr key={permiso.id}>
                            <td className="col-modulo">
                              <div className="permiso-nombre">{permiso.nombre}</div>
                              <div className="permiso-accion">{modulo}.{permiso.accion}</div>
                            </td>
                            <td className="col-checkbox">
                              <input
                                type="checkbox"
                                checked={asignados.has(permiso.id)}
                                disabled={!hasPermission('roles.assign_permissions')}
                                onChange={() => togglePermiso(permiso.id)}
                              />
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasChanges() && hasPermission('roles.assign_permissions') && (
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                  {' '}Guardar cambios
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleDiscard}
                  disabled={saving}
                >
                  <i className="fas fa-undo"></i> Descartar
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default AsignacionPermisos;
